<template>
    <q-card flat square class="tk-case-note-history">
        <div class="tk-case-note-history__top-card">
            <header class="tk-case-note-history__header">
                <div class="tk-case-note-history__title">
                    <h2>Case notes</h2>
                    <span>{{ filteredNotes.length }} of {{ notes.length }}</span>
                </div>
                <div class="tk-case-note-history__actions">
                    <q-btn v-if="canCreate" unelevated dense no-caps flat color="primary" icon="add" label="Add note" @click="$emit('add')" />
                </div>
            </header>

            <section class="tk-case-note-history__controls">
                <q-input
                    ref="searchInput"
                    v-model="searchTerm"
                    outlined
                    dense
                    clearable
                    debounce="200"
                    placeholder="Search narrative, location, or people..."
                    aria-label="Search case notes"
                >
                    <template #prepend>
                        <q-icon name="search" size="20px" />
                    </template>
                </q-input>

                <button class="tk-case-note-history__filter-toggle" type="button" @click="showFilters = !showFilters">
                    <q-icon :name="showFilters ? 'expand_more' : 'chevron_right'" />
                    <span>Filters</span>
                </button>

                <div v-show="showFilters" class="tk-case-note-history__filters">
                    <q-select
                        v-model="contactTypeFilter"
                        :options="contactTypeOptions"
                        outlined
                        dense
                        clearable
                        emit-value
                        map-options
                        label="Contact type — any"
                    />
                    <q-select
                        v-model="workerFilter"
                        :options="workerOptions"
                        outlined
                        dense
                        clearable
                        emit-value
                        map-options
                        label="Worker — any"
                    />
                    <q-select
                        v-model="wellbeingFilter"
                        :options="wellbeingOptions"
                        outlined
                        dense
                        clearable
                        emit-value
                        map-options
                        label="Pou — any"
                    />
                    <q-input
                        v-model="fromDate"
                        outlined
                        dense
                        clearable
                        stack-label
                        type="date"
                        label="From date"
                    />
                    <q-input
                        v-model="toDate"
                        outlined
                        dense
                        clearable
                        stack-label
                        type="date"
                        label="To date"
                    />
                    <q-checkbox
                        v-model="riskOnly"
                        class="tk-case-note-history__risk-filter"
                        label="Risk assessments only"
                        color="primary"
                    />
                </div>
            </section>
        </div>

        <section class="tk-case-note-history__body">
            <q-inner-loading :showing="loading" />
            <div v-if="!loading && !filteredNotes.length" class="tk-case-note-history__empty">
                <q-icon name="o_speaker_notes_off" size="28px" />
                <span>{{ notes.length ? "No case notes match these filters." : "No case notes have been added." }}</span>
            </div>

            <template v-else>
                <TkCaseNoteCard
                    v-for="note in filteredNotes"
                    :key="note.id"
                    :note="note"
                    @edit="$emit('edit', $event)"
                />
            </template>
        </section>
    </q-card>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import TkCaseNoteCard from "@/components/TkClients/TkCaseNoteCard.vue";

const noteTimestamp = (note) => {
    const timestamp = new Date(note.contact_at).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
};

const noteDateValue = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = String(date.getFullYear());
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const noteWellbeingAreas = (note) => {
    if (note.wellbeing_areas?.length) return note.wellbeing_areas;
    if (!note.wellbeing_title) return [];
    return [{ id: note.wellbeing_id, title: note.wellbeing_title }];
};

const noteSearchText = (note) => [
    note.contact_type,
    note.contact_at,
    note.staff_name,
    note.staff_email,
    note.contact_location,
    note.location,
    note.contact_method,
    note.body,
    ...noteWellbeingAreas(note).map(area => area.title),
    ...(note.with_whom_options || []).map(option => option.label),
    ...(note.risk_assessments || []).map(risk => risk.label),
    ...(note.additional_staff || []).flatMap(staff => [staff.staff_name, staff.staff_email]),
]
    .filter(value => value !== null && value !== undefined)
    .join(" ")
    .toLocaleLowerCase();

const uniqueOptions = (values) => [...new Set(values.filter(Boolean))]
    .sort((first, second) => first.localeCompare(second))
    .map(value => ({ label: value, value }));

const workerValue = (note) => String(note.staff_id || note.staff_email || note.staff_name || "");

export default {
    name: "TkCaseNotesHistoryPanel",
    components: {
        TkCaseNoteCard,
    },
    emits: ["add", "count-changed", "edit"],
    props: {
        canCreate: { type: Boolean, default: false },
        personId: {
            type: [Number, String],
            required: true,
        },
        engagementId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            notes: [],
            searchTerm: "",
            showFilters: true,
            contactTypeFilter: null,
            workerFilter: null,
            wellbeingFilter: null,
            fromDate: "",
            toDate: "",
            riskOnly: false,
        };
    },
    computed: {
        contactTypeOptions() {
            return uniqueOptions(this.notes.map(note => note.contact_type));
        },
        workerOptions() {
            const workers = this.notes.reduce((options, note) => {
                const value = workerValue(note);
                if (!value || options.some(option => option.value === value)) return options;
                return [...options, { label: note.staff_name || note.staff_email || "-", value }];
            }, []);
            return workers.sort((first, second) => first.label.localeCompare(second.label));
        },
        wellbeingOptions() {
            return uniqueOptions(this.notes.flatMap(note => (
                noteWellbeingAreas(note).map(area => area.title)
            )));
        },
        filteredNotes() {
            const searchTerm = (this.searchTerm || "").trim().toLocaleLowerCase();
            return [...this.notes]
                .filter(note => !searchTerm || noteSearchText(note).includes(searchTerm))
                .filter(note => !this.contactTypeFilter || note.contact_type === this.contactTypeFilter)
                .filter(note => !this.workerFilter || workerValue(note) === this.workerFilter)
                .filter(note => !this.wellbeingFilter || (
                    noteWellbeingAreas(note).some(area => area.title === this.wellbeingFilter)
                ))
                .filter(note => !this.fromDate || noteDateValue(note.contact_at) >= this.fromDate)
                .filter(note => !this.toDate || noteDateValue(note.contact_at) <= this.toDate)
                .filter(note => !this.riskOnly || !!note.risk_assessments?.length)
                .sort((first, second) => noteTimestamp(second) - noteTimestamp(first));
        },
    },
    async mounted() {
        await this.loadNotes();
        this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    methods: {
        async loadNotes() {
            this.loading = true;
            try {
                const response = await api.tkPeople.listCaseNotes(this.personId, this.engagementId);
                this.notes = response.data || [];
                this.$emit("count-changed", this.notes.length);
            } catch (error) {
                notify.error("Failed to load case notes", "top");
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-case-note-history { display: flex; flex-direction: column; width: 100%;  min-height: 420px; padding: 16px 24px 28px; box-sizing: border-box; background: transparent; color: #46544e; }
.tk-case-note-history__top-card { min-width: 0;  border: 1px solid rgba(47, 62, 70, 0.08); border-radius: 8px; background: rgba(255, 255, 255, 0.97); box-shadow: 0 8px 20px rgba(47, 62, 70, 0.05);}
.tk-case-note-history__header { display: flex;  align-items: center; justify-content: space-between; gap: 16px; min-height: 60px; padding: 10px 18px; border-bottom: 1px solid rgba(47, 62, 70, 0.08); }
.tk-case-note-history__title, .tk-case-note-history__actions { display: flex; align-items: center; }
.tk-case-note-history__title { gap: 8px; }
.tk-case-note-history__title h2 { margin: 0; color: #3d4b46; font-size: 1.15rem; font-weight: 600; letter-spacing: 0.035em; }
.tk-case-note-history__title span { color: #718079; font-size: 0.86rem; }
.tk-case-note-history__actions { gap: 8px; }
.tk-case-note-history__controls { padding: 18px; max-width: 1100px; }
.tk-case-note-history__filter-toggle { display: inline-flex; align-items: center; gap: 4px; margin: 12px 0 8px; padding: 2px 4px; border: 0; border-radius: 6px; background: transparent; color: #53615a; cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 500; }
.tk-case-note-history__filter-toggle:hover { background: rgba(77, 143, 119, 0.08); }
.tk-case-note-history__filters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 14px; border-radius: 10px;}
.tk-case-note-history__controls :deep(.q-field__control) { border-radius: 9px; }
.tk-case-note-history__risk-filter { min-height: 40px; padding: 0 4px; }
.tk-case-note-history__body { position: relative; display: flex; flex: 1 1 auto; flex-direction: column; gap: 12px; padding-top: 16px; }
.tk-case-note-history__empty { display: flex; align-items: center; justify-content: center; gap: 10px; min-height: 160px; color: #6f7c75; }

@media (max-width: 700px) {
    .tk-case-note-history { padding: 12px; }
    .tk-case-note-history__header { align-items: flex-start; padding: 10px 12px; }
    .tk-case-note-history__filters { grid-template-columns: 1fr; }
    .tk-case-note-history__controls { padding: 14px 12px; }
}
</style>
